import React, { useEffect, useState } from 'react';
import adminApi from '../services/adminApi';

function PromoPage() {
  const [promoMeals, setPromoMeals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingById, setEditingById] = useState({});
  const [savingById, setSavingById] = useState({});
  const [deletingById, setDeletingById] = useState({});
  const [copiedById, setCopiedById] = useState({});

  useEffect(() => {
    loadPromoMeals();
  }, []);

  const loadPromoMeals = async () => {
    try {
      setIsLoading(true);
      setError('');
      const response = await adminApi.getPromoMeals();
      const meals = response.promoMeals || [];
      setPromoMeals(meals);

      const initialEditState = {};
      meals.forEach((meal) => {
        initialEditState[meal._id] = meal.visualDescription || '';
      });
      setEditingById(initialEditState);
    } catch (err) {
      setError(err.message || 'Failed to load promo meals');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditChange = (promoMealId, value) => {
    setEditingById((prev) => ({
      ...prev,
      [promoMealId]: value,
    }));
  };

  const handleSave = async (promoMealId) => {
    try {
      setSavingById((prev) => ({ ...prev, [promoMealId]: true }));
      const visualDescription = editingById[promoMealId] || '';
      const response = await adminApi.updatePromoMealVisualDescription(promoMealId, visualDescription);
      const updatedPromoMeal = response.promoMeal;

      setPromoMeals((prev) =>
        prev.map((item) => (item._id === promoMealId ? updatedPromoMeal : item))
      );
    } catch (err) {
      alert(err.message || 'Failed to save visual description');
    } finally {
      setSavingById((prev) => ({ ...prev, [promoMealId]: false }));
    }
  };

  const handleDelete = async (promoMealId) => {
    const confirmed = window.confirm('Delete this promo meal?');
    if (!confirmed) return;

    try {
      setDeletingById((prev) => ({ ...prev, [promoMealId]: true }));
      await adminApi.deletePromoMeal(promoMealId);
      setPromoMeals((prev) => prev.filter((item) => item._id !== promoMealId));
      setEditingById((prev) => {
        const next = { ...prev };
        delete next[promoMealId];
        return next;
      });
    } catch (err) {
      alert(err.message || 'Failed to delete promo meal');
    } finally {
      setDeletingById((prev) => ({ ...prev, [promoMealId]: false }));
    }
  };

  const handleOpenLanding = (promoMealId) => {
    if (!promoMealId) return;
    window.open(`/promo/${promoMealId}`, '_blank', 'noopener,noreferrer');
  };

  const handleCopyLandingLink = async (promoMealId) => {
    if (!promoMealId) return;

    const absoluteUrl = `${window.location.origin}/promo/${promoMealId}`;

    try {
      await navigator.clipboard.writeText(absoluteUrl);
      setCopiedById((prev) => ({ ...prev, [promoMealId]: true }));
      setTimeout(() => {
        setCopiedById((prev) => ({ ...prev, [promoMealId]: false }));
      }, 1500);
    } catch {
      window.prompt('Copy landing URL:', absoluteUrl);
    }
  };

  if (isLoading) {
    return (
      <div className="admin-loading">
        <div className="admin-spinner"></div>
        <p>Loading promo meals...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="admin-page-header">
        <h1>Promo</h1>
        <p>Manage meals shown in promotional surfaces</p>
      </div>

      {error && (
        <div className="admin-card" style={{ border: '1px solid #fecaca', background: '#fef2f2' }}>
          <p style={{ margin: 0, color: '#b91c1c' }}>{error}</p>
        </div>
      )}

      {promoMeals.length === 0 ? (
        <div className="admin-card admin-empty-state">
          <p>No promo meals yet.</p>
          <p style={{ marginTop: '0.5rem' }}>Admins can add meals from the review screen using Add to Promo.</p>
        </div>
      ) : (
        <div className="admin-promo-grid">
          {promoMeals.map((meal) => {
            const isSaving = !!savingById[meal._id];
            const isDeleting = !!deletingById[meal._id];
            const visualValue = editingById[meal._id] || '';
            const isCopied = !!copiedById[meal._id];

            return (
              <div key={meal._id} className="admin-card admin-promo-card">
                <div className="admin-promo-image-wrap">
                  {meal.imageURL ? (
                    <img
                      src={meal.imageURL}
                      alt={meal.title}
                      className="admin-promo-image"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="admin-promo-image-fallback">No image</div>
                  )}
                </div>

                <h3 className="admin-promo-title">{meal.title}</h3>
                <p className="admin-promo-meta">
                  {meal.mealType || 'Unknown'} • {new Date(meal.createdAt).toLocaleString()}
                </p>

                <div className="admin-form-group">
                  <label>Visual Description</label>
                  <textarea
                    rows={8}
                    value={visualValue}
                    onChange={(e) => handleEditChange(meal._id, e.target.value)}
                    placeholder="Enter visual description"
                    disabled={isSaving || isDeleting}
                  />
                </div>

                <div className="admin-promo-actions">
                  <button
                    className="admin-btn admin-btn-secondary"
                    onClick={() => handleOpenLanding(meal._id)}
                    disabled={isSaving || isDeleting}
                  >
                    Open Landing
                  </button>
                  <button
                    className="admin-btn admin-btn-secondary"
                    onClick={() => handleCopyLandingLink(meal._id)}
                    disabled={isSaving || isDeleting}
                  >
                    {isCopied ? 'Copied!' : 'Copy Landing URL'}
                  </button>
                  <button
                    className="admin-btn admin-btn-primary"
                    onClick={() => handleSave(meal._id)}
                    disabled={isSaving || isDeleting}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    className="admin-btn admin-btn-danger"
                    onClick={() => handleDelete(meal._id)}
                    disabled={isSaving || isDeleting}
                  >
                    {isDeleting ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default PromoPage;
